/** SUNTRONIX ONE — ONE GROUP establishment badge.
 *  Renders a small colour-coded pill for a record's issuing establishment.
 *  Labels are fetched once and cached at module level (internal users only).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

let _cache = null;
let _inflight = null;
const _subs = new Set();

async function loadLabels() {
  if (_cache) return _cache;
  if (!_inflight) {
    _inflight = api
      .get("/establishments/labels")
      .then(({ data }) => {
        _cache = data?.labels || {};
        _subs.forEach((fn) => fn(_cache));
        return _cache;
      })
      .catch(() => {
        _cache = {};
        return _cache;
      });
  }
  return _inflight;
}

export default function EstablishmentBadge({ id, className = "" }) {
  const [labels, setLabels] = useState(_cache);

  useEffect(() => {
    let active = true;
    const fn = (l) => active && setLabels({ ...l });
    _subs.add(fn);
    loadLabels().then((l) => active && setLabels({ ...l }));
    return () => { active = false; _subs.delete(fn); };
  }, []);

  if (!id) return <span className="text-[11px] text-slate-400">—</span>;
  const meta = labels?.[id];
  const color = meta?.primary_color || "#2962FF";
  const text = meta?.short_name || meta?.display_name || "—";
  return (
    <span
      data-testid={`establishment-badge-${id}`}
      title={meta?.display_name || id}
      className={"inline-flex items-center gap-1 h-5 px-1.5 rounded text-[10px] font-semibold border " + className}
      style={{ color, borderColor: color + "55", backgroundColor: color + "12" }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {text}
    </span>
  );
}
